import {
  collection,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./types').NoSQLUser } NoSQLUser
 */

/**
 * A top level function to keep track of common inputs to all functions.
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @returns {object} The functions to access the users collection
 */
export default function usersCollectionFns(db) {
  /**
   * @param {string} userId The id of the user to find
   * @returns {Promise<NoSQLUser|null>} A user or nothing
   */
  async function getUserById(userId) {
    try {
      const docRef = doc(db, Collections.USERS, userId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists() || docSnap.data().deletedAt) {
        throw new Error("No user data available");
      }
      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Failed to get user by ID:", error);
      throw error;
    }
  }

  /**
   * @param {NoSQLUser} user A user to save
   * @returns {Promise<string>} Returns the user ID
   */
  async function saveUser(user) {
    const { id: userId, ...userData } = user;

    console.log("SAVE USER USER", user);
    console.log("SAVE USER ID", userId);
    console.log("SAVE USER DATA", userData);

    const cleanedUserData = {
      ...userData,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      deletedAt: null,
    };

    try {
      if (userId) {
        await setDoc(doc(db, Collections.USERS, userId), cleanedUserData);
        return userId;
      } else {
        const docRef = await addDoc(
          collection(db, Collections.USERS),
          cleanedUserData,
        );
        return docRef.id;
      }
    } catch (error) {
      console.error("Failed to save user:", error);
      throw error;
    }
  }

  /**
   * @param {string} userId The id of the user to delete
   * @param {string} deletedBy The id of the user who is deleting
   * @returns {Promise<void>} Nothing
   */
  async function deleteUser(userId, deletedBy) {
    try {
      await updateDoc(doc(db, Collections.USERS, userId), {
        deletedAt: serverTimestamp(),
        deletedBy,
      });
    } catch (error) {
      console.error("Failed to delete user:", error);
      throw error;
    }
  }

  /**
   * Updates the user's active organization
   * @param {string} userId The id of the user
   * @param {string} organizationId The id of the organization to set as active
   * @returns {Promise<void>} Nothing
   */
  async function updateUserActiveOrg(userId, organizationId) {
    try {
      await updateDoc(doc(db, Collections.USERS, userId), {
        "metadata.activeOrganizationId": organizationId,
      });
    } catch (error) {
      console.error("Failed to update user active organization:", error);
      throw error;
    }
  }

  /**
   * Updates user profile fields
   * @param {string} userId The id of the user to update
   * @param {object} profileUpdates The profile fields to update
   * @param {string} [profileUpdates.displayName] New display name
   * @returns {Promise<void>} Nothing
   */
  async function updateUserProfile(userId, profileUpdates) {
    try {
      const updates = {};
      if (profileUpdates.displayName !== undefined) {
        updates["profile.displayName"] = profileUpdates.displayName;
      }
      await updateDoc(doc(db, Collections.USERS, userId), updates);
    } catch (error) {
      console.error("Failed to update user profile:", error);
      throw error;
    }
  }

  return {
    getUserById,
    saveUser,
    deleteUser,
    updateUserActiveOrg,
    updateUserProfile,
  };
}
